import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { leadUploadService } from '../../client_contact_stages/verve-design/services/leadUploadService';
import { BASE_URL } from '@/lib/API';
import { Download, Eye, Loader2, File, Image as ImageIcon, FileText, Trash2, Check } from 'lucide-react';
import { fmtDate } from "@/lib/formatters";
import { showSuccessMessage, showErrorMessage } from "@/lib/toast";
import { TableActionButton, DataTable } from "@/components/ui";
import { projectService } from '../services/projectService';

export const GalleryView = ({ project, category = 'gallery', onApprove }) => {
  const leadId = project?.leadId?._id || project?.leadId;
  const { data, isLoading, error } = useQuery({
    queryKey: ['lead-uploads', leadId],
    queryFn: async () => {
      if (!leadId) return [];
      const res = await leadUploadService.getFilesByLead(leadId);
      return res.data?.data?.files || [];
    },
    enabled: !!leadId,
  });

  const leadFiles = data || [];

  const projectDocs = (project?.mergedDocuments || []).map(doc => ({
    _id: doc._id || doc.path,
    isProjectDoc: true,
    source: doc.source,
    mimetype: doc.originalName?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 
              doc.originalName?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? 'image/jpeg' : 'application/octet-stream',
    filename: doc.originalName || doc.path?.split('/').pop() || 'Document',
    title: doc.originalName || doc.path?.split('/').pop() || 'Document',
    type: doc.source === 'design' ? (doc.stage || doc.type || 'PROD DRAWINGS') : 
          doc.source === 'work_update' ? (doc.stage || doc.type || 'WORK UPDATE') :
          doc.source === 'gallery' ? (doc.stage || doc.type || 'PROGRESS IMAGES') :
          (doc.type || doc.stage || 'PROJECT DOC'),
    size: null,
    uploadedBy: typeof doc.uploadedBy === 'object' ? doc.uploadedBy : { name: doc.uploadedBy || 'System' },
    createdAt: doc.uploadedAt || project.createdAt,
    path: doc.path
  }));

  const isDesignFile = (file) => {
    if (file.source === 'design') return true;
    const t = (file.type || file.stage || '').toString().toLowerCase().replace(/[-_]/g, ' ');
    return t.includes('design') || t.includes('drawing') || t.includes('sign of copy') || t.includes('prod') || t.includes('2d') || t.includes('3d') || t.includes('measure');
  };

  // Combine and sort by date descending
  const allFiles = [...leadFiles, ...projectDocs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const files = allFiles.filter(file => {
    if (category === 'designs') {
      return isDesignFile(file);
    } else if (category === 'documents') {
      return !isDesignFile(file);
    } else if (category === 'gallery') {
      const isImg = file.mimetype?.toLowerCase().includes('image') || 
                    file.filename?.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i) || 
                    file.originalName?.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i);
      return Boolean(isImg);
    }
    return true;
  });

  const handleDownload = async (file) => {
    if (file.isProjectDoc) {
      try {
        const response = await fetch(`${BASE_URL}${file.path}`);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.originalName || file.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        window.open(`${BASE_URL}${file.path}`, '_blank');
      }
    } else {
      const downloadUrl = `${BASE_URL}/api/lead-uploads/stream/${file.fileId}?download=1`;
      window.location.href = downloadUrl;
      showSuccessMessage("File downloaded successfully!");
    }
  };

  const handlePreview = (file) => {
    if (file.isProjectDoc) {
      window.open(`${BASE_URL}${file.path}`, '_blank');
    } else {
      const previewUrl = `${BASE_URL}/api/lead-uploads/stream/${file.fileId}`;
      window.open(previewUrl, '_blank');
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    
    try {
      if (file.isProjectDoc) {
        await projectService.deleteDocument(project._id, file._id);
      } else {
        await leadUploadService.deleteFile(file.fileId);
      }
      showSuccessMessage('Document deleted successfully');
      
      if (typeof window.refreshProject === 'function') {
         window.refreshProject();
      } else {
         window.location.reload();
      }
    } catch (error) {
      showErrorMessage('Failed to delete document');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const columns = [
    {
      header: 'File Name',
      key: 'filename',
      className: "text-[12px] font-medium text-slate-700 font-['Poppins']",
      render: (row) => (
        <div className="flex flex-col py-1">
          <span className="font-semibold text-slate-700 truncate max-w-[250px]" title={row.title || row.filename}>
            {row.title || row.filename}
          </span>
          {row.title && row.title !== row.filename && (
            <span className="text-[10px] text-slate-400 truncate max-w-[250px] mt-0.5">{row.filename}</span>
          )}
        </div>
      )
    },
    {
      header: 'Stage',
      key: 'type',
      render: (row) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-bold uppercase tracking-wider">
          {row.type?.replace(/_/g, ' ') || '—'}
        </span>
      )
    },
    {
      header: 'Size',
      key: 'size',
      render: (row) => (
        <span className="text-[12px] font-medium text-slate-500 font-['Poppins']">
          {formatSize(row.size)}
        </span>
      )
    },
    {
      header: 'Uploaded By',
      key: 'uploadedBy',
      render: (row) => (
        <span className="text-[12px] font-medium text-slate-600 font-['Poppins']">
          {row.uploadedBy?.name || 'System'}
        </span>
      )
    },
    {
      header: 'Date',
      key: 'createdAt',
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-500 font-['Poppins'] whitespace-nowrap">
          {fmtDate(row.createdAt)}
        </span>
      )
    },
    {
      header: 'Actions',
      key: 'actions',
      className: 'text-right',
      render: (row) => (
        <div className="flex items-center justify-end gap-1.5 py-1">
          {onApprove && !row.isApproved && row.isProjectDoc && (
            <TableActionButton 
              icon={Check} 
              variant="success" 
              onClick={(e) => { e.stopPropagation(); onApprove(row); }}
              title="Approve Document"
            />
          )}
          <TableActionButton 
            icon={Eye} 
            variant="primary" 
            onClick={(e) => { e.stopPropagation(); handlePreview(row); }}
            title="View"
          />
          <TableActionButton 
            icon={Download} 
            variant="secondary" 
            onClick={(e) => { e.stopPropagation(); handleDownload(row); }}
            title="Download"
          />
          <TableActionButton 
            icon={Trash2} 
            variant="danger" 
            onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
            title="Delete"
          />
        </div>
      )
    }
  ];

  return (
    <DataTable 
      columns={columns}
      data={files}
      isLoading={isLoading}
      noScroll={true}
      wrapperClassName=""
    />
  );
};
